/**
 * ============================================================================
 * CATALOG VIEW — derive a view config from the definitions catalog (R4.4).
 * ============================================================================
 *
 * For dashboards without a hand-authored view config ('catalog' in their
 * structure file — Quality today): one table-sections tab, one section per
 * distinct metricGroup, groups and metrics in catalog sortOrder. The
 * dashboard renders the day its definitions publish, with zero FE change;
 * an empty catalog yields a view with one empty tab (the standard empty
 * state), never an error.
 *
 * Pure function — the caller passes the already-fetched catalog.
 */

import { MetricDefinition } from 'src/types/metricsApi.types';
import { MetricTableSectionConfig, WbrView } from 'src/types';

/** Matches the hand-authored views' window (detailedSummaryView.json). */
const DEFAULT_WEEK_WINDOW_SIZE = 9;

/** Stable section id from a group name ("OTD Metrics" → "otd-metrics"). */
const sectionId = (group: string) =>
  group
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'metrics';

export function buildCatalogView(
  definitions: MetricDefinition[] | undefined,
): WbrView {
  const ordered = (definitions ?? [])
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  // Group in first-seen (sortOrder) order; a blank group still gets a section
  // so no published definition can silently vanish from the page.
  const byGroup = new Map<string, MetricTableSectionConfig>();
  for (const definition of ordered) {
    const group = definition.metricGroup || 'Metrics';
    const section = byGroup.get(group) ?? {
      id: sectionId(group),
      title: group,
      metrics: [],
    };
    section.metrics.push(definition.name);
    byGroup.set(group, section);
  }

  return {
    viewId: 'catalog',
    displayName: 'Detailed Summary',
    defaultWeekWindowSize: DEFAULT_WEEK_WINDOW_SIZE,
    tabs: [
      {
        id: 'catalog-summary',
        label: 'Detailed Summary',
        type: 'table-sections',
        sections: Array.from(byGroup.values()),
      },
    ],
  };
}
